import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import DraftBanner from "@/components/DraftBanner";
import useFormDraft from "@/lib/useFormDraft";
import { ArrowLeft, Upload, Sparkles, Loader2, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

const DRAFT_KEY = "ica_draft_persona";

const empty = {
  name: "",
  description: "",
  avatar_url: "",
  is_default: false,
};

/**
 * Full-page persona editor. Mirrors the visual language of CreateCharacter.
 * Routes: /create-persona  (new)   and   /edit-persona/:id  (edit).
 * Back navigation uses nav(-1) so hitting the browser Back button returns
 * the user to wherever they came from (Discover, Profile, ChatView, etc.),
 * matching the behaviour of CreateCharacter.
 */
export default function CreatePersona() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const { form, setForm, restoredAt, clearDraft, discardDraft } =
    useFormDraft(DRAFT_KEY, empty, { enabled: !isEdit });
  const [imgPrompt, setImgPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEdit) {
      api.get("/personas")
        .then(({ data }) => {
          const p = (data.personas || []).find((x) => x.id === editId);
          if (!p) {
            toast.error("Persona not found.");
            nav(-1);
            return;
          }
          setForm({
            name: p.name || "",
            description: p.description || "",
            avatar_url: p.avatar_url || "",
            is_default: !!p.is_default,
          });
        })
        .catch(() => toast.error("Could not load persona."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, isEdit, nav]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  async function onUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image too large (max 4MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, avatar_url: reader.result }));
    reader.readAsDataURL(file);
  }

  async function generateImage() {
    if (!imgPrompt.trim()) {
      toast.error("Describe your persona first.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/generate/avatar", { prompt: imgPrompt });
      setForm((f) => ({ ...f, avatar_url: data.avatar_url }));
      toast.success("Portrait generated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  }

  function addQuickFillTag(tag) {
    const line = `\n${tag}: `;
    setForm((f) => ({ ...f, description: (f.description || "") + line }));
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/personas/${editId}`, form);
        toast.success("Persona updated.");
      } else {
        await api.post("/personas", form);
        toast.success("Persona created.");
        clearDraft();
      }
      // Return to whichever page the user came from (Profile tab, Discover,
      // Chat setup, etc.). Using nav(-1) mirrors CreateCharacter's flow.
      nav(-1);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function del() {
    if (!isEdit) return;
    if (!confirm(`Delete ${form.name || "this persona"}? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/personas/${editId}`);
      toast.success("Persona deleted.");
      nav(-1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6">
      <header className="mb-6 flex items-center gap-3">
        <button
          data-testid="create-persona-back-btn"
          onClick={() => nav(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="font-display text-xs uppercase tracking-[0.35em] text-fuchsia-400/80">{isEdit ? "Edit" : "New"}</div>
          <h1 className="font-display text-3xl leading-tight text-white">{isEdit ? "Edit Persona" : "Create Persona"}</h1>
        </div>
        {isEdit && (
          <button
            type="button"
            data-testid="create-persona-delete-btn"
            onClick={del}
            disabled={deleting}
            className="flex h-10 items-center gap-1.5 rounded-full bg-rose-500/10 border border-rose-500/30 px-3 text-xs text-rose-200 hover:bg-rose-500/20 disabled:opacity-50"
          >
            <Trash2 size={12} /> Delete
          </button>
        )}
      </header>

      <p className="mb-5 text-sm text-neutral-400">
        A persona is who <em>you</em> are in the story. Pick one when starting a chat, or switch mid-story.
      </p>

      <DraftBanner
        restoredAt={restoredAt}
        onDiscard={discardDraft}
        testId="create-persona-draft-banner"
      />

      <form onSubmit={submit} className="space-y-5" data-testid="create-persona-form">
        <Section title="Portrait">
          <div className="flex items-start gap-4">
            <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/5">
              {form.avatar_url ? (
                <img src={form.avatar_url} alt="avatar" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-display text-3xl text-white/30">
                  {form.name?.[0] || "?"}
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <label className="btn-pill btn-ghost w-full cursor-pointer" data-testid="create-persona-upload-label">
                <Upload size={16} />
                Upload image
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="create-persona-upload-input" />
              </label>
              <div className="flex gap-2">
                <input
                  data-testid="create-persona-img-prompt"
                  value={imgPrompt}
                  onChange={(e) => setImgPrompt(e.target.value)}
                  placeholder="Describe yourself to generate a portrait…"
                  className="input-field flex-1 !py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={generateImage}
                  disabled={generating}
                  data-testid="create-persona-generate-btn"
                  className="btn-pill btn-primary text-sm px-4 disabled:opacity-50"
                >
                  {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {generating ? "" : "Imagine"}
                </button>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Basics">
          <Field label="Name">
            <input required data-testid="create-persona-name-input" value={form.name} onChange={set("name")} className="input-field" placeholder="Your character's name" />
          </Field>
          <Field label="About you (in-character)">
            <textarea
              data-testid="create-persona-description-input"
              value={form.description}
              onChange={set("description")}
              rows={7}
              placeholder="Age, appearance, personality, quirks…"
              maxLength={2250}
              className="input-field"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {["Gender", "Appearance", "Species", "Age", "Personality", "Occupation"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  data-testid={`create-persona-quickfill-${tag.toLowerCase()}`}
                  onClick={() => addQuickFillTag(tag)}
                  className="inline-flex items-center gap-1 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1 text-[11px] text-fuchsia-200 hover:bg-fuchsia-500/20"
                >
                  + {tag}
                </button>
              ))}
            </div>
            <div className="mt-1 text-right text-[10px] text-neutral-500">
              {(form.description || "").length}/2250
            </div>
          </Field>
        </Section>

        <Section title="Preferences">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm text-white flex items-center gap-1.5">
                <Star size={14} className={form.is_default ? "text-yellow-400 fill-yellow-400" : "text-neutral-400"} />
                Set as default persona
              </div>
              <div className="text-xs text-neutral-500">Automatically selected when you start new chats.</div>
            </div>
            <input
              type="checkbox"
              data-testid="create-persona-default-checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              className="mt-1 h-5 w-5 accent-fuchsia-500"
            />
          </div>
        </Section>

        {error && (
          <div data-testid="create-persona-error" className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          data-testid="create-persona-submit-btn"
          className="btn-pill btn-primary w-full py-3.5 disabled:opacity-50"
        >
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create persona"}
        </button>
      </form>

      <BottomNav />

      <style>{`
        .input-field {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
          padding: 0.75rem 1rem;
          color: white;
          font-family: 'Outfit', sans-serif;
          outline: none;
          resize: vertical;
        }
        .input-field:focus { border-color: rgba(217,70,239,0.5); background: rgba(255,255,255,0.06); }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <h2 className="mb-3 text-xs uppercase tracking-[0.25em] text-neutral-500">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-neutral-400">{label}</span>
      {children}
    </label>
  );
}
